import { useState, useEffect } from 'react';
import { getReferralsByUsername, updateUser, getAllUsersFromStorage } from '../../../../utils/sharedStorage';

const COUNTRY_CONFIG: Record<string, { currency: string; activationCost: number }> = {
  'CI': { currency: 'FCFA', activationCost: 3500 },
  'SN': { currency: 'FCFA', activationCost: 3500 },
  'BJ': { currency: 'FCFA', activationCost: 3500 },
  'ML': { currency: 'FCFA', activationCost: 3500 },
  'TG': { currency: 'FCFA', activationCost: 3500 },
  'BF': { currency: 'FCFA', activationCost: 3500 },
  'GA': { currency: 'FCFA', activationCost: 3500 },
  'CM': { currency: 'FCFA', activationCost: 3500 },
  'CG': { currency: 'FCFA', activationCost: 3500 },
  'CD': { currency: 'FC', activationCost: 9800 },
  'GH': { currency: 'GH₵', activationCost: 35 },
};

const DAY_TASKS: Record<number, { label: string; icon: string; color: string; day: string }> = {
  0: { label: 'Questions de culture générale', icon: 'ri-question-answer-line', color: 'text-yellow-700', day: 'Lundi' },
  1: { label: 'Vidéos TikTok', icon: 'ri-tiktok-line', color: 'text-pink-600', day: 'Mardi' },
  2: { label: 'Vidéos YouTube', icon: 'ri-youtube-line', color: 'text-red-600', day: 'Mercredi' },
  3: { label: 'Clics publicitaires', icon: 'ri-cursor-line', color: 'text-orange-600', day: 'Jeudi' },
  4: { label: 'Vidéos Instagram', icon: 'ri-instagram-line', color: 'text-pink-500', day: 'Vendredi' },
};

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'];
const DEFAULT_VIDEO_URL = 'https://www.youtube.com/@LudovicSalenne';

function getYoutubeEmbedUrl(url: string): string | null {
  if (!url) return null;
  const watchMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (watchMatch) return `https://www.youtube.com/embed/${watchMatch[1]}?autoplay=0&rel=0`;
  const embedMatch = url.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/);
  if (embedMatch) return `https://www.youtube.com/embed/${embedMatch[1]}?autoplay=0&rel=0`;
  const shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/);
  if (shortsMatch) return `https://www.youtube.com/embed/${shortsMatch[1]}?autoplay=0&rel=0`;
  return null;
}

function lsGet<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch { return fallback; }
}

export default function DashboardHome() {
  const [userData, setUserData] = useState({
    name: '', username: '', balance: 0, currency: 'FCFA',
    isActivated: false, totalEarnings: 0, totalWithdrawals: 0,
    tasksCompleted: 0, activationCost: 3500, phone: '',
  });
  const [whatsapp, setWhatsapp] = useState('');
  const [whatsappSaved, setWhatsappSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [totalAllProfits, setTotalAllProfits] = useState(0);
  const [referralStats, setReferralStats] = useState({
    totalReferrals: 0, activatedReferrals: 0, pendingReferrals: 0,
    totalCommissions: 0, level1Count: 0, level2Count: 0, level3Count: 0,
  });
  const [realBalance, setRealBalance] = useState(0);
  const [balanceTaches, setBalanceTaches] = useState(0);
  const [balancePoints, setBalancePoints] = useState(0);
  const [dashboardVideoUrl, setDashboardVideoUrl] = useState(DEFAULT_VIDEO_URL);

  // ── Calcul du solde depuis localStorage uniquement ──────────────────────
  const calculateRealBalance = () => {
    try {
      const raw = localStorage.getItem('currentUser');
      if (!raw) return;
      let currentUser: any = {};
      try { currentUser = JSON.parse(raw); } catch { return; }
      const myPhone = currentUser.phone || '';

      // Solde parrainage depuis user storage (le plus frais)
      let parrainageBalance = 0;
      if (myPhone) {
        const freshUser =
          lsGet<any>(`SUCCESSLINK_V1__user_${myPhone}`, null) ||
          lsGet<any>(`user_${myPhone}`, null);
        if (freshUser && typeof freshUser.balance === 'number') {
          parrainageBalance = freshUser.balance;
          // Sync currentUser
          currentUser.balance = parrainageBalance;
          localStorage.setItem('currentUser', JSON.stringify(currentUser));
        } else {
          parrainageBalance = Number(currentUser.balance) || 0;
        }
      } else {
        parrainageBalance = Number(currentUser.balance) || 0;
      }

      // Gains tâches et points depuis userProfits
      let tachesBalance = 0;
      let pointsBalance = 0;
      try {
        const profits: Array<{ amount: number; type: string; phone?: string }> =
          lsGet('userProfits', []);
        const myProfits = profits.filter(p => !p.phone || p.phone === myPhone);

        tachesBalance = myProfits
          .filter(p => {
            const t = (p.type || '').toLowerCase();
            return t.includes('tâche') || t.includes('tache');
          })
          .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

        pointsBalance = myProfits
          .filter(p => {
            const t = (p.type || '').toLowerCase();
            return t.includes('points') || t.includes('contacts') || t.includes('roue');
          })
          .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
      } catch { /* ignore */ }

      const totalProfits = parrainageBalance + tachesBalance + pointsBalance;

      setRealBalance(parrainageBalance);
      setBalanceTaches(tachesBalance);
      setBalancePoints(pointsBalance);
      setTotalAllProfits(totalProfits);
      setUserData(prev => ({
        ...prev,
        balance: parrainageBalance,
        totalEarnings: totalProfits,
      }));
    } catch (error) {
      console.error('Erreur calculateRealBalance:', error);
    }
  };

  const loadUserData = () => {
    try {
      const raw = localStorage.getItem('currentUser');
      if (!raw) return;
      let p: any = {};
      try { p = JSON.parse(raw); } catch { return; }

      const country = p.country || 'CI';
      const config = COUNTRY_CONFIG[country] || COUNTRY_CONFIG['CI'];
      const isActivated = p.isActivated === true || p.activated === true || p.accountStatus === 'active';

      let completedTasks: number[] = [];
      try {
        const parsed = lsGet<number[]>('completedTasks', []);
        completedTasks = Array.isArray(parsed) ? parsed : [];
      } catch { completedTasks = []; }

      let totalWithdrawals = 0;
      try {
        const withdrawals: Array<{ amount: number; status: string; userPhone?: string }> =
          lsGet('withdrawalRequests', []);
        if (Array.isArray(withdrawals)) {
          totalWithdrawals = withdrawals
            .filter(w => w.status === 'validated' && w.userPhone === p.phone)
            .reduce((s, w) => s + (Number(w.amount) || 0), 0);
        }
      } catch { totalWithdrawals = 0; }

      let savedWa = '';
      try { savedWa = localStorage.getItem('userWhatsapp') || ''; } catch { savedWa = ''; }
      setWhatsapp(savedWa);
      setWhatsappSaved(!!savedWa);
      setTotalExpenses(isActivated ? config.activationCost : 0);

      setUserData({
        name: p.name || '',
        username: p.username || p.name || '',
        balance: 0,
        currency: config.currency,
        isActivated,
        totalEarnings: 0,
        totalWithdrawals,
        tasksCompleted: completedTasks.length,
        activationCost: config.activationCost,
        phone: p.phone || '',
      });

      calculateRealBalance();
    } catch (error) {
      console.error('Erreur loadUserData:', error);
    }
  };

  const loadReferralStats = async (silent = false) => {
    try {
      const raw = localStorage.getItem('currentUser');
      if (!raw) return;
      let p: any = {};
      try { p = JSON.parse(raw); } catch { return; }
      const username = p.username || p.name || '';
      if (!username) return;

      const allReferrals = await getReferralsByUsername(username);
      if (!Array.isArray(allReferrals)) return;

      const activatedReferrals = allReferrals.filter(r => r.isActivated || r.accountStatus === 'active');
      const pendingReferrals = allReferrals.filter(r => !r.isActivated && r.accountStatus !== 'active');

      let level2Count = 0;
      let level3Count = 0;
      let totalCommissions = activatedReferrals.length * 1500;

      for (const filleul of allReferrals) {
        if (!filleul.username) continue;
        try {
          const level2Users = await getReferralsByUsername(filleul.username);
          if (!Array.isArray(level2Users)) continue;
          level2Count += level2Users.length;
          const level2Activated = level2Users.filter(u => u.isActivated || u.accountStatus === 'active');
          totalCommissions += level2Activated.length * 600;

          for (const level2 of level2Users) {
            if (!level2.username) continue;
            try {
              const level3Users = await getReferralsByUsername(level2.username);
              if (!Array.isArray(level3Users)) continue;
              level3Count += level3Users.length;
              const level3Activated = level3Users.filter(u => u.isActivated || u.accountStatus === 'active');
              totalCommissions += level3Activated.length * 400;
            } catch { /* continuer */ }
          }
        } catch { /* continuer */ }
      }

      setReferralStats({
        totalReferrals: allReferrals.length + level2Count + level3Count,
        activatedReferrals: activatedReferrals.length,
        pendingReferrals: pendingReferrals.length,
        totalCommissions,
        level1Count: allReferrals.length,
        level2Count,
        level3Count,
      });
    } catch (error) {
      console.error('Erreur loadReferralStats:', error);
    }
  };

  useEffect(() => {
    loadUserData();
    loadReferralStats();

    const savedVideo = localStorage.getItem('dashboardVideoUrl');
    if (savedVideo) setDashboardVideoUrl(savedVideo);

    // Écouter les mises à jour de solde en temps réel
    let balanceChannel: BroadcastChannel | null = null;
    try {
      balanceChannel = new BroadcastChannel('balance_update');
      balanceChannel.onmessage = (event) => {
        const raw = localStorage.getItem('currentUser');
        if (!raw) return;
        try {
          const cu = JSON.parse(raw);
          if (event.data?.phone === cu.phone) {
            calculateRealBalance();
          }
        } catch { /* ignore */ }
      };
    } catch { /* BroadcastChannel non supporté */ }

    // Écouter l'activation du compte
    let activationChannel: BroadcastChannel | null = null;
    try {
      activationChannel = new BroadcastChannel('account_activation');
      activationChannel.onmessage = () => {
        loadUserData();
      };
    } catch { /* ignore */ }

    // Polling toutes les 10 secondes
    const interval = setInterval(() => {
      calculateRealBalance();
      loadReferralStats(true);
      const v = localStorage.getItem('dashboardVideoUrl');
      if (v) setDashboardVideoUrl(v);
    }, 10000);

    return () => {
      clearInterval(interval);
      try { balanceChannel?.close(); } catch { /* ignore */ }
      try { activationChannel?.close(); } catch { /* ignore */ }
    };
  }, []);

  const referralCode = userData.username;
  const savedDomain = localStorage.getItem('referralDomain') || window.location.origin;
  const referralLink = `${savedDomain}/inscription?ref=${referralCode}`;
  const fullReferralText = `${referralCode} — ${referralLink}`;

  const fallbackCopy = (text: string) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '-9999px';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try { document.execCommand('copy'); } catch { /* ignore */ }
    document.body.removeChild(textarea);
  };

  const handleCopyLink = () => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(referralLink).catch(() => fallbackCopy(referralLink));
    } else {
      fallbackCopy(referralLink);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyCode = () => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(referralCode).catch(() => fallbackCopy(referralCode));
    } else {
      fallbackCopy(referralCode);
    }
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleSaveWhatsapp = async () => {
    if (!whatsapp.trim()) return;
    try { localStorage.setItem('userWhatsapp', whatsapp.trim()); } catch { /* ignore */ }
    try {
      const raw = localStorage.getItem('currentUser') || '{}';
      const user = JSON.parse(raw);
      if (user.phone) await updateUser(user.phone, { whatsapp: whatsapp.trim() });
    } catch { /* ignore */ }
    setWhatsappSaved(true);
  };

  const handleShareWhatsApp = () => {
    const text = encodeURIComponent(`🚀 Rejoins SUCCESSLINK AGENCY avec mon code *${referralCode}* !\n👉 ${referralLink}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const handleShareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`, '_blank');
  };

  const handleShareInstagram = () => {
    fallbackCopy(fullReferralText);
    window.open('https://www.instagram.com/', '_blank');
  };

  const todayJS = new Date().getDay();
  const activeDayIndex = todayJS >= 1 && todayJS <= 5 ? todayJS - 1 : 0;
  const embedUrl = getYoutubeEmbedUrl(dashboardVideoUrl);

  return (
    <div className="space-y-5 pb-8">

      {/* ===== BANDEAU BIENVENUE ===== */}
      <div className="rounded-2xl bg-gradient-to-br from-yellow-400 via-yellow-300 to-yellow-200 p-6 shadow-lg border border-yellow-300">
        <h2 className="text-2xl font-extrabold text-yellow-900 mb-2 flex items-center gap-2">
          <i className="ri-star-smile-line text-yellow-700 text-3xl"></i>
          Bienvenue à nouveau, {userData.username || userData.name || 'Leader'} !
        </h2>
        <p className="text-yellow-800 text-sm leading-relaxed max-w-2xl">
          🚀 Vous avez rejoint une communauté d'élite ! Ici, chaque action que vous posez vous rapproche de votre liberté financière.
          Accomplissez vos tâches quotidiennes, parrainez vos proches et regardez vos revenus croître chaque jour.
          <strong> Votre succès commence maintenant — ne laissez passer aucune opportunité !</strong>
        </p>
      </div>

      {/* ===== 2 CARTES DÉPENSES / PROFITS ===== */}
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-xl bg-gradient-to-br from-gray-700 to-gray-600 p-5 text-white shadow-md flex items-center gap-4">
          <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full">
            <i className="ri-arrow-down-circle-line text-2xl"></i>
          </div>
          <div>
            <p className="text-gray-200 text-xs font-semibold uppercase tracking-wide">Dépenses</p>
            <p className="text-2xl font-extrabold">
              {userData.isActivated ? userData.activationCost.toLocaleString() : '0'}
              <span className="text-sm font-normal ml-1">{userData.currency}</span>
            </p>
            {userData.isActivated && <p className="text-gray-300 text-xs mt-0.5">Activation du compte</p>}
          </div>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-orange-500 to-orange-400 p-5 text-white shadow-md flex items-center gap-4">
          <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full">
            <i className="ri-arrow-up-circle-line text-2xl"></i>
          </div>
          <div>
            <p className="text-orange-100 text-xs font-semibold uppercase tracking-wide">Profits</p>
            <p className="text-2xl font-extrabold">
              {totalAllProfits.toLocaleString()}
              <span className="text-sm font-normal ml-1">{userData.currency}</span>
            </p>
            <p className="text-orange-200 text-xs mt-0.5">
              Parrainage ({realBalance.toLocaleString()}) + Tâches ({balanceTaches.toLocaleString()}) + Points ({balancePoints.toLocaleString()})
            </p>
          </div>
        </div>
      </div>

      {/* ===== CADRE WHATSAPP ===== */}
      {!whatsappSaved && (
        <div className="rounded-2xl bg-gradient-to-br from-teal-700 to-teal-500 p-6 shadow-lg text-white">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full">
              <i className="ri-whatsapp-line text-2xl"></i>
            </div>
            <div>
              <h3 className="font-bold text-lg">Ton numéro WhatsApp</h3>
              <p className="text-teal-200 text-xs">Enregistre ton numéro pour rester en contact avec l'équipe</p>
            </div>
          </div>
          <div className="flex gap-2">
            <input
              type="tel"
              value={whatsapp}
              onChange={e => setWhatsapp(e.target.value)}
              placeholder="Ex: +2250700000000"
              className="flex-1 rounded-xl px-4 py-3 text-gray-800 text-sm font-medium outline-none bg-white"
            />
            <button
              onClick={handleSaveWhatsapp}
              className="bg-yellow-400 hover:bg-yellow-300 text-yellow-900 font-bold px-5 py-3 rounded-xl transition whitespace-nowrap cursor-pointer"
            >
              Enregistrer
            </button>
          </div>
        </div>
      )}

      {/* ===== 3 CARTES : SOLDE / TÂCHES / RETRAITS ===== */}
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-2xl bg-gradient-to-b from-orange-500 to-orange-400 p-5 text-white shadow-md flex flex-col items-center justify-center text-center gap-2">
          <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full">
            <i className="ri-wallet-3-line text-2xl"></i>
          </div>
          <p className="text-orange-100 text-xs font-semibold uppercase tracking-wide">Solde actuel</p>
          <p className="text-3xl font-extrabold">{totalAllProfits.toLocaleString()}</p>
          <p className="text-orange-200 text-xs">{userData.currency}</p>
          <p className="text-orange-100 text-xs leading-tight">
            Parrainage: {realBalance.toLocaleString()} + Tâches: {balanceTaches.toLocaleString()} + Points: {balancePoints.toLocaleString()}
          </p>
          <button
            onClick={calculateRealBalance}
            className="mt-1 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-semibold transition cursor-pointer whitespace-nowrap flex items-center gap-1"
          >
            <i className="ri-refresh-line"></i>
            Actualiser
          </button>
        </div>

        <div className="rounded-2xl bg-white border-2 border-gray-200 p-5 shadow-md flex flex-col items-center justify-center text-center gap-2">
          <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-full">
            <i className="ri-task-line text-2xl text-gray-600"></i>
          </div>
          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Points de tâches</p>
          <p className="text-3xl font-extrabold text-gray-800">{userData.tasksCompleted}</p>
          <p className="text-gray-400 text-xs">Tâches accomplies</p>
        </div>

        <div className="rounded-2xl bg-gradient-to-b from-teal-500 to-teal-400 p-5 text-white shadow-md flex flex-col items-center justify-center text-center gap-2">
          <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full">
            <i className="ri-bank-card-line text-2xl"></i>
          </div>
          <p className="text-teal-100 text-xs font-semibold uppercase tracking-wide">Retraits totaux</p>
          <p className="text-3xl font-extrabold">{userData.totalWithdrawals.toLocaleString()}</p>
          <p className="text-teal-100 text-xs">{userData.currency}</p>
        </div>
      </div>

      {/* ===== PARRAINAGE ===== */}
      <div className="bg-white rounded-2xl shadow-md p-5 border border-gray-100">
        <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2 text-base">
          <i className="ri-user-add-line text-orange-500 text-xl"></i>
          Parrainage — Invitez vos proches
        </h3>
        <div className="space-y-4">
          <div>
            <p className="text-xs text-gray-500 mb-2 font-medium">🎫 Votre code d'invitation</p>
            <div className="flex items-center gap-2 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl px-4 py-3 border-2 border-orange-300">
              <span className="flex-1 text-xl font-extrabold text-orange-600 tracking-widest">{referralCode || '—'}</span>
              <button
                onClick={handleCopyCode}
                className="bg-orange-500 hover:bg-orange-400 text-white text-xs font-bold px-4 py-2 rounded-lg transition whitespace-nowrap cursor-pointer flex items-center gap-1"
              >
                <i className={copiedCode ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                {copiedCode ? 'Copié !' : 'Copier'}
              </button>
            </div>
          </div>

          <div>
            <p className="text-xs text-gray-500 mb-2 font-medium">🔗 Lien d'invitation complet</p>
            <div className="flex flex-col gap-2 bg-gray-50 rounded-xl px-4 py-3 border border-gray-200">
              <span className="text-xs text-gray-700 break-all font-mono">{referralLink}</span>
              <button
                onClick={handleCopyLink}
                className="w-full bg-orange-500 hover:bg-orange-400 text-white text-xs font-bold px-4 py-2 rounded-lg transition whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                {copied ? '✅ Lien copié !' : "Copier le lien d'invitation"}
              </button>
            </div>
          </div>

          <div>
            <p className="text-xs text-gray-500 mb-2 font-medium">📲 Partager directement sur</p>
            <div className="grid grid-cols-3 gap-3">
              <button onClick={handleShareWhatsApp} className="flex flex-col items-center justify-center gap-1.5 bg-green-500 hover:bg-green-400 text-white font-bold py-3 rounded-xl transition shadow cursor-pointer whitespace-nowrap">
                <div className="w-8 h-8 flex items-center justify-center"><i className="ri-whatsapp-line text-2xl"></i></div>
                <span className="text-xs">WhatsApp</span>
              </button>
              <button onClick={handleShareFacebook} className="flex flex-col items-center justify-center gap-1.5 bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 rounded-xl transition shadow cursor-pointer whitespace-nowrap">
                <div className="w-8 h-8 flex items-center justify-center"><i className="ri-facebook-circle-line text-2xl"></i></div>
                <span className="text-xs">Facebook</span>
              </button>
              <button onClick={handleShareInstagram} className="flex flex-col items-center justify-center gap-1.5 bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 hover:opacity-90 text-white font-bold py-3 rounded-xl transition shadow cursor-pointer whitespace-nowrap">
                <div className="w-8 h-8 flex items-center justify-center"><i className="ri-instagram-line text-2xl"></i></div>
                <span className="text-xs">Instagram</span>
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1.5 text-center">Instagram : le lien est copié, collez-le dans votre bio ou story</p>
          </div>

          <p className="text-xs text-gray-400 text-center">Niveau 1 : 1 500 F &bull; Niveau 2 : 600 F &bull; Niveau 3 : 400 F</p>
        </div>
      </div>

      {/* ===== VIDÉO DU MOMENT ===== */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-red-50 to-orange-50">
          <div className="w-9 h-9 flex items-center justify-center bg-red-100 rounded-full">
            <i className="ri-youtube-line text-xl text-red-600"></i>
          </div>
          <div>
            <p className="font-bold text-gray-800 text-sm">Vidéo du moment</p>
            <p className="text-xs text-gray-500">Regardez la vidéo de votre coach</p>
          </div>
          <a
            href={dashboardVideoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition whitespace-nowrap cursor-pointer"
          >
            <i className="ri-external-link-line"></i>
            Ouvrir
          </a>
        </div>
        {embedUrl ? (
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              src={embedUrl}
              title="Vidéo du tableau de bord"
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ border: 'none' }}
            ></iframe>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-10 px-6 bg-gradient-to-br from-gray-50 to-red-50">
            <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-full mb-4">
              <i className="ri-youtube-line text-4xl text-red-500"></i>
            </div>
            <p className="font-bold text-gray-800 mb-1 text-center">Chaîne YouTube de votre coach</p>
            <p className="text-xs text-gray-500 mb-4 text-center">Abonnez-vous pour ne manquer aucune formation</p>
            <a
              href={dashboardVideoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-3 rounded-xl transition cursor-pointer whitespace-nowrap"
            >
              <i className="ri-youtube-line text-lg"></i>
              Voir la chaîne YouTube
            </a>
          </div>
        )}
      </div>

      {/* ===== TÂCHES DE LA SEMAINE ===== */}
      <div className="rounded-2xl bg-gradient-to-br from-yellow-100 via-white to-yellow-50 border-2 border-yellow-300 p-6 shadow-lg">
        <h3 className="text-xl font-extrabold text-yellow-800 mb-1 flex items-center gap-2">
          <i className="ri-calendar-check-line text-yellow-600 text-2xl"></i>
          Tâches de la semaine
        </h3>
        <p className="text-yellow-700 text-xs mb-5">Chaque jour de la semaine a sa propre tâche. Accomplissez-la pour gagner 150F !</p>

        <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
          {DAYS.map((day, i) => (
            <div
              key={day}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-bold border transition ${
                i === activeDayIndex
                  ? 'bg-yellow-400 text-yellow-900 border-yellow-400 shadow'
                  : 'bg-white text-gray-500 border-gray-200'
              }`}
            >
              {day}
            </div>
          ))}
        </div>

        <div className="space-y-3">
          {DAYS.map((day, i) => {
            const task = DAY_TASKS[i];
            const isToday = i === activeDayIndex;
            return (
              <div
                key={i}
                className={`flex items-center gap-4 rounded-xl px-5 py-4 shadow-sm border transition ${
                  isToday ? 'bg-yellow-400 border-yellow-400 shadow-md' : 'bg-white border-yellow-100 opacity-60'
                }`}
              >
                <div className={`w-11 h-11 flex items-center justify-center rounded-full border ${isToday ? 'bg-white border-yellow-300' : 'bg-yellow-50 border-yellow-200'}`}>
                  <i className={`${task.icon} text-2xl ${task.color}`}></i>
                </div>
                <div className="flex-1">
                  <p className={`font-bold text-sm ${isToday ? 'text-yellow-900' : 'text-gray-700'}`}>{task.label}</p>
                  <p className={`text-xs ${isToday ? 'text-yellow-800' : 'text-gray-400'}`}>{day} — Récompense : 150F</p>
                </div>
                {isToday && (
                  <span className="bg-white text-yellow-700 text-xs font-extrabold px-3 py-1 rounded-full whitespace-nowrap shadow">
                    ✅ Aujourd'hui
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
}
